<template>
  <template v-if="model.state.section.enabled && allowRenderParticles">
    <teleport :to="model.state.section.selectorClass">
      <Particles
        id="tsparticles"
        class="tsparticles tsparticles__in-section"
        :particles-init="particlesInit"
        :options="particlesConfig"
      />
    </teleport>
  </template>
  <template v-else-if="model.state.flowpage.enabled && allowRenderParticles">
    <teleport to=".flow">
      <Particles
        id="tsparticles"
        class="tsparticles tsparticles__in-flowpage"
        :particles-init="particlesInit"
        :options="particlesConfig"
      />
    </teleport>
  </template>
  <template v-else-if="model.state.body.enabled && allowRenderParticles">
    <teleport to="body">
      <Particles id="tsparticles" class="tsparticles" :particles-init="particlesInit" :options="particlesConfig" />
    </teleport>
  </template>
</template>

<script lang="ts">
import { computed, defineComponent, PropType } from 'vue';
import {
  IntegrationParticleGeneratorModel,
  ParticleGeneratorLocation,
  ParticleGeneratorVariant
} from '@/components/integrations/particle-generator/Model';
import { loadFull } from 'tsparticles';
import { Engine, ISourceOptions } from 'tsparticles-engine';
import { useStore } from '@/store';
import {
  getConfettiConfig,
  getCustomConfig,
  getFlagsConfig,
  getSnowConfig
} from '@/components/integrations/particle-generator/particle-configs';

export default defineComponent({
  name: 'View',
  props: {
    model: {
      type: Object as PropType<IntegrationParticleGeneratorModel>,
      required: true
    }
  },
  setup(props) {
    const store = useStore();

    const particlesInit = async (engine: Engine) => {
      await loadFull(engine);
    };

    let baseConfig: ISourceOptions = {
      autoPlay: true,
      background: {
        color: {
          value: 'none'
        },
        opacity: 1
      },
      fullScreen: {
        enable: true,
        zIndex: -1
      }
    };

    if (props.model.state.section.enabled && baseConfig.fullScreen) {
      baseConfig.fullScreen = {
        enable: false,
        zIndex: -1
      };
    }

    const particlesConfig = computed<ISourceOptions>(() => {
      if (props.model.state.variant === ParticleGeneratorVariant.CUSTOM) {
        return getCustomConfig(props.model.state);
      }
      if (props.model.state.variant === ParticleGeneratorVariant.CONFETTI) {
        return getConfettiConfig(props.model.state);
      }
      if (props.model.state.variant === ParticleGeneratorVariant.FLAGS) {
        return getFlagsConfig(props.model.state);
      }
      return getSnowConfig(props.model.state);
    });

    const allowRenderParticles = computed(() => {
      const globalState = store.getters['global/state'];
      if (props.model.state.body?.enabled) {
        if (props.model.state.body.onSpecificFlowPages && props.model.state.body.flowPages) {
          return props.model.state.body.flowPages?.some((flowPageId) =>
            globalState.mountedFlowPages?.includes(flowPageId)
          );
        }

        return true;
      }
      if (props.model.state.flowpage.enabled) {
        return props.model.state.flowpage.ids?.some((flowPageId) => globalState.mountedFlowPages?.includes(flowPageId));
      }
      return !!(
        props.model.state.section.enabled &&
        props.model.state.section.id &&
        globalState.mountedSections?.includes(props.model.state.section.id)
      );
    });

    return {
      ParticleGeneratorLocation,
      allowRenderParticles,
      particlesInit,
      particlesConfig
    };
  }
});
</script>

<style lang="scss">
.tsparticles {
  opacity: 0;
  animation-name: fadeIn;
  animation-duration: 1s;
  animation-fill-mode: forwards;
  animation-timing-function: ease;

  .site--iframe.site--popover-active.site--mobile & {
    display: none;
  }
}
.tsparticles {
  &__in-section,
  &__in-flowpage {
    position: absolute;
    top: 0;
    bottom: 0;
    left: 0;
    right: 0;
    z-index: 20;
    pointer-events: none;
  }
}
</style>
